/**
 * @file media.ts
 *
 * Media API for the shop admin, over the real `/v1/webspace` endpoints. {@link uploadImage}
 * posts a product/category gallery image (raw bytes; the filename is the last path segment)
 * and returns the `media_assets` id used in `imageIds`. {@link uploadDownloadFile} posts a
 * digital-product download file to the `/v1` digital-products endpoint. {@link fetchMediaLibrary}
 * reads the shop's recorded media (the picker's "select from library" list) and
 * {@link deleteMediaAsset} removes one by its media-asset id. Uploads go through {@link apiFetch}
 * directly (raw bytes); the list/delete use the enveloped `/v1` helpers.
 */

import { apiFetch } from '../../api/client';
import { V1_API_BASE, v1Get, v1Mutate } from './v1-request';
import { WcApiError } from './types';
import type { WebspaceMediaItemV1 } from './v1-types';

/** An uploaded image: its `media_assets` id (UUID) and public URL. */
export interface UploadedImage {
  id: string;
  src: string;
}

/** An uploaded download file: its `media_assets` id (UUID) and public download URL. */
export interface UploadedDownload {
  id: string;
  src: string;
}

/** A recorded media-library item as the picker consumes it (`id` is the media-asset UUID). */
export interface LibraryMediaItem {
  id: string;
  url: string;
  filename: string;
  mimeType: string;
  createdAt: string;
}

/** Page size for the media-library fetch, and the cap on how many items are loaded. */
const MEDIA_PAGE_SIZE = 100;
const MEDIA_MAX_ITEMS = 500;

/**
 * Reduces a filename to the safe leaf the upload routes accept (`[A-Za-z0-9._-]`),
 * falling back to `fallback` when the name has no usable characters.
 */
function safeFilename(name: string, fallback: string): string {
  const cleaned = name.replace(/[^A-Za-z0-9._-]/g, '_');
  return cleaned === '' || cleaned === '.' || cleaned === '..' ? fallback : cleaned;
}

/** Maps a `/v1` webspace media item to the picker's {@link LibraryMediaItem} shape. */
function toLibraryMediaItem(item: WebspaceMediaItemV1): LibraryMediaItem {
  return {
    id: item.mediaAssetId,
    url: item.url,
    filename: item.filename,
    mimeType: item.mimeType,
    createdAt: item.createdAt,
  };
}

/**
 * Uploads a product gallery image to the webspace and returns its media-asset id.
 *
 * @param file - The image file to upload.
 *
 * @returns The uploaded image's `media_assets` id (for `imageIds`) and public URL.
 *
 * @throws {WcApiError} When the upload fails.
 */
export async function uploadImage(file: File): Promise<UploadedImage> {
  const response = await apiFetch(
    `${V1_API_BASE}/webspace/image/${safeFilename(file.name, 'image')}`,
    { method: 'POST', headers: { 'Content-Type': file.type }, body: file },
  );
  const body = (await response.json().catch(() => null)) as
    | { data?: { mediaAssetId: string; url: string }; error?: { code?: string; message?: string } }
    | null;
  if (!response.ok || !body?.data) {
    throw new WcApiError(
      body?.error?.message ?? `Image upload failed (${response.status})`,
      response.status,
      body?.error?.code ?? 'media_upload_failed',
    );
  }
  return { id: body.data.mediaAssetId, src: body.data.url };
}

/**
 * Uploads a digital-product download file and returns its media-asset id.
 *
 * Posts the raw bytes to the `/v1` digital-products endpoint (the filename is the
 * last path segment, sanitized) and returns the recorded `media_assets` id used in a
 * product's `download.mediaIds`, plus the public download URL (the GET-by-id route).
 *
 * @param file - The download file to upload.
 *
 * @returns The uploaded file's `media_assets` id and its download URL.
 *
 * @throws {WcApiError} When the upload fails.
 */
export async function uploadDownloadFile(file: File): Promise<UploadedDownload> {
  const filesBase = `${V1_API_BASE}/digital-products/file`;
  const response = await apiFetch(`${filesBase}/${safeFilename(file.name, 'file')}`, {
    method: 'POST',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  const body = (await response.json().catch(() => null)) as
    | { data?: { mediaAssetId: string }; error?: { code?: string; message?: string } }
    | null;
  if (!response.ok || !body?.data) {
    throw new WcApiError(
      body?.error?.message ?? `File upload failed (${response.status})`,
      response.status,
      body?.error?.code ?? 'media_upload_failed',
    );
  }
  // The GET-by-id download route — a usable preview/download link for the uploaded file.
  return { id: body.data.mediaAssetId, src: `${filesBase}/${body.data.mediaAssetId}` };
}

/**
 * Fetches the shop's recorded media library (newest-first), following the offset pages
 * up to {@link MEDIA_MAX_ITEMS}. The `/v1` list supports only `page`/`pageSize` (no
 * server-side search/filter/sort), so the picker filters and sorts the loaded set
 * client-side.
 *
 * @returns The media-library items (media-asset id, url, filename, mime type, created-at).
 *
 * @throws {WcApiError} When a request fails.
 */
export async function fetchMediaLibrary(): Promise<LibraryMediaItem[]> {
  const all: LibraryMediaItem[] = [];
  let page = 1;
  for (;;) {
    const res = await v1Get<WebspaceMediaItemV1[]>(
      `/webspace/media?page=${page}&pageSize=${MEDIA_PAGE_SIZE}`,
    );
    all.push(...res.data.map(toLibraryMediaItem));
    const reachedLastPage = res.totalPages > 0 && page >= res.totalPages;
    if (res.data.length < MEDIA_PAGE_SIZE || reachedLastPage || all.length >= MEDIA_MAX_ITEMS) {
      break;
    }
    page += 1;
  }
  return all.slice(0, MEDIA_MAX_ITEMS);
}

/**
 * Deletes a recorded media asset by its id (removes the webspace file and soft-deletes
 * the `media_assets` row). Idempotent server-side.
 *
 * @param id - The media-asset id (UUID).
 *
 * @throws {WcApiError} When the request fails.
 */
export async function deleteMediaAsset(id: string): Promise<void> {
  await v1Mutate(`/webspace/media/${id}`, 'DELETE');
}
